/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  getTimelinePosition,
  formatTime,
  timeToMinutes,
} from "../utils";
import EmployeeQuickView from "./EmployeeQuickView";
import { useAvailability, isEmployeeAvailable } from "../hooks/useAvailability";
import useAssignmentsForLocation from '../hooks/useAssignmentsForLocation';
import { toDate, toIsoDateString } from "../utils/shiftDate";

// Timeline constants
const TIMELINE_WIDTH = 1200; // Total width of timeline in pixels
const LABEL_WIDTH = 180; // Width of the left label column (slightly reduced)
const ROW_HEIGHT = 44; // Height of each employee row (reduced to show more rows)
const HEADER_HEIGHT = 56; // Height of time header (reduced)

const WEEKDAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Format minutes into "Hh Mm" (e.g. 8h 30m) or "0h" when zero
function formatMinutesToHours(mins) {
  const total = Number(mins || 0);
  const hours = total / 60;
  // always show two decimal places
  const formatted = hours.toFixed(2);
  return `${formatted} hrs`;
}

// compute total minutes for an employee across the provided weekDays
function computeEmployeeWeekMinutes(empId, effectiveShifts, weekDays) {
  if (!empId || !Array.isArray(weekDays) || weekDays.length === 0) return 0;
  const dayKeys = new Set((weekDays || []).map(d => require('../utils/shiftDate').toIsoDateString(new Date(d))));
  return (effectiveShifts || []).reduce((acc, s) => {
    try { 
  if (!s || (!s.shiftDate && !s.shiftDateKey)) return acc;
  const { toDate } = require('../utils/shiftDate');
  const sd = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
  const key = require('../utils/shiftDate').toIsoDateString(sd);
      if (!dayKeys.has(key)) return acc;
      if (s.employeeId !== empId) return acc;
      const st = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
      const en = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
      let mins = en - st;
      if (mins < 0) mins = (24 * 60 - st) + en;
      return acc + (mins || 0);
    } catch (err) { return acc; }
  }, 0);
}

// Employee status helpers
function isTerminatedEmployee(emp) {
  if (!emp) return false;
  const statusRaw = (
    emp?.employment?.currentStatus ||
    emp?.employmentStatus ||
    emp?.status ||
    emp?.currentStatus
  );
  const status = (statusRaw == null ? '' : String(statusRaw)).toLowerCase();
  const terminatedFlag = !!(emp.terminated || emp.isTerminated);
  const archivedFlag = !!(emp.archived || emp.isArchived);
  return terminatedFlag || archivedFlag || status === 'terminated' || status === 'inactive';
}

// NOTE: Time range presets were removed per UX decision. The timeline visible
// range will now be driven by the selected location's Scheduling Hours and
// will automatically expand when shifts extend outside that range.

// Draggable Shift Component for Timeline with resize handles
function DraggableTimelineShift({ 
  shift, 
  roleMap, 
  isDragOverlay = false, 
  onShiftResize,
  onShiftContextMenu,
  onDragStart: onDragStartProp,
  onDragEnd: onDragEndProp,
  visibleMinutes,
  rangeStartMinutes,
  rangeEndMinutes,
  containerWidth = TIMELINE_WIDTH,
  isOpenShift = false,
  isWeekPublished = false,
  conflictMode = false,
  layoutTick = 0,
  topOffset = null,
  shiftInnerHeight: shiftInnerHeightProp = null,
  availability = null,
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [isDraggingTime, setIsDraggingTime] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [optimisticTimes, setOptimisticTimes] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  // lightweight refs for click-vs-drag detection
  const mouseDownRef = useRef(null);
  const movedRef = useRef(false);

  // Cleanup function to ensure event listeners are removed and state is cleared
  useEffect(() => {
    return () => {
      // Clear any hanging optimistic state or previews on unmount
      setOptimisticTimes(null);
      setDragPreview(null);
      setIsResizing(false);
      setIsDraggingTime(false);
      setResizeHandle(null);
      setUpdateInProgress(false);
    };
  }, []);
  
  // Clear optimistic state when shift data changes externally (to prevent ghost shifts)
  useEffect(() => {
    if (optimisticTimes && !isDraggingTime && !isResizing && !isUpdating) {
      // Check if the shift times now match the optimistic times
      const timesMatch = optimisticTimes.start === shift.shiftStartTime && 
                        optimisticTimes.end === shift.shiftEndTime;
      
      if (timesMatch) {
        // Times match, clear optimistic state immediately
        setOptimisticTimes(null);
      } else {
        // Times don't match, wait a bit longer for potential updates
        const timer = setTimeout(() => {
          setOptimisticTimes(null);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [shift.shiftStartTime, shift.shiftEndTime, optimisticTimes, isDraggingTime, isResizing, isUpdating]);
  
  // Clear optimistic state when shift ID changes (new shift), but not on updatedAt changes
  useEffect(() => {
    setOptimisticTimes(null);
  }, [shift.id]);
  
  // Use optimistic times with smoother logic
  const currentStartTime = (() => {
    if (optimisticTimes && (isDraggingTime || isResizing || isUpdating)) {
      return optimisticTimes.start;
    }
    // If we have optimistic times and they match the shift times, use optimistic to prevent flash
    if (optimisticTimes && optimisticTimes.start === shift.shiftStartTime) {
      return optimisticTimes.start;
    }
    return shift.shiftStartTime;
  })();
  
  const currentEndTime = (() => {
    if (optimisticTimes && (isDraggingTime || isResizing || isUpdating)) {
      return optimisticTimes.end;
    }
    // If we have optimistic times and they match the shift times, use optimistic to prevent flash
    if (optimisticTimes && optimisticTimes.end === shift.shiftEndTime) {
      return optimisticTimes.end;
    }
    return shift.shiftEndTime;
  })();

  // visual opacity is controlled in sx below

  const roleData = Object.prototype.hasOwnProperty.call(roleMap, shift.role) ? roleMap[shift.role] : undefined;
  const roleColor = roleData?.color || "#888";
  // Prefer the authoritative role document name. If missing, fall back to the
  // human-readable name stored on the shift (denormalized) or the raw role id.
  const roleBaseName = roleData.name || shift.roleName || shift.role;
  const roleDataIsEmpty = !roleData || (typeof roleData === 'object' && Object.keys(roleData).length === 0);
  const isArchivedRole = !!roleData?.archived || (roleDataIsEmpty && !!shift.roleName) || !!shift.archived;
  const roleName = isArchivedRole ? `${roleBaseName} (Archived)` : roleBaseName;
  const shiftColor = isOpenShift || shift.isOpenShift ? "#9E9E9E" : roleColor;
  const shiftBorderStyle = isOpenShift || shift.isOpenShift ? "dashed" : "solid";

  // Pending state flags (match grid behavior)
  // Show draft/yellow appearance for any shift that is pending creation or explicitly not published.
  // This matches the behavior in the grid view so unpublished shifts are consistently highlighted
  // until they are published, regardless of the week's published state.
  const hasPendingUpdate = isWeekPublished && shift.pendingUpdate && Object.keys(shift.pendingUpdate || {}).length > 0;
  const isDraftNew = (shift.pendingCreate === true || shift.isPublished === false || hasPendingUpdate === true);
  const isPendingDelete = isWeekPublished && shift.pendingDelete === true && shift.isPublished === true;

  // Check availability for visual cue. Only consider outside-availability when
  // an availability array is actually present (non-empty) either on the shift
  // itself or from the parent `availability` hook. This avoids flagging shifts
  // as outside availability when no availability data exists.
    let isOutsideAvailability = false;
    try {
      const { toDate } = require('../utils/shiftDate');
      const shiftDate = shift.shiftDateKey ? toDate(shift.shiftDateKey) : toDate(shift.shiftDate);
      const dayOfWeek = shiftDate.getDay();
    const sTime = currentStartTime || shift.shiftStartTime || shift.startTime || '00:00';
    const eTime = currentEndTime || shift.shiftEndTime || shift.endTime || '00:00';
    // Prefer availability attached to the shift, otherwise use availability passed from parent hook
    const availSource = Array.isArray(shift.__availability) ? shift.__availability : availability;
    const hasAvailability = Array.isArray(availSource) && availSource.length > 0;
    if (typeof isEmployeeAvailable === 'function' && hasAvailability) {
      try {
        isOutsideAvailability = !isEmployeeAvailable(availSource, shift.employeeId, dayOfWeek, sTime, eTime, shift.shiftDate || shift.shiftDate?.toDate?.());
      } catch (err) {
        isOutsideAvailability = false;
      }
    }

  // removed debug logging
  } catch (err) {
    isOutsideAvailability = false;
  }

  // Calculate position and width based on current times (optimistic or actual)
  const { left, width, isVisible } = useMemo(() => {
  // include layoutTick in the closure so ESLint knows it's intentionally used to force recompute
  void layoutTick; // ensure layoutTick is referenced for recompute without creating an unused variable
  const startMinutes = timeToMinutes(currentStartTime);
  const endMinutes = timeToMinutes(currentEndTime);
    
    // Check if shift overlaps with visible range
    if (endMinutes <= rangeStartMinutes || startMinutes >= rangeEndMinutes) {
      return { left: 0, width: 0, isVisible: false };
    }
    
    // Calculate clipped start and end within visible range
    const clippedStartMinutes = Math.max(startMinutes, rangeStartMinutes);
    const clippedEndMinutes = Math.min(endMinutes, rangeEndMinutes);
    
    // Convert to timeline position
    const left = getTimelinePosition(
      `${String(Math.floor(clippedStartMinutes / 60)).padStart(2,'0')}:${String(clippedStartMinutes % 60).padStart(2,'0')}`,
      visibleMinutes,
      containerWidth,
      rangeStartMinutes
    );

    const right = getTimelinePosition(
      `${String(Math.floor(clippedEndMinutes / 60)).padStart(2,'0')}:${String(clippedEndMinutes % 60).padStart(2,'0')}`,
      visibleMinutes,
      containerWidth,
      rangeStartMinutes
    );
    
    const width = Math.max(0, right - left);
    
    return { left: Math.max(0, left), width, isVisible: true };
  }, [currentStartTime, currentEndTime, visibleMinutes, rangeStartMinutes, rangeEndMinutes, containerWidth, layoutTick]); // ESLint: dependencies

  const handleResizeStart = useCallback((handle) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeHandle(handle);
    
    const startX = e.clientX;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      let newStartTime = currentStartTime;
      let newEndTime = currentEndTime;
      
      if (handle === 'start') {
  const newLeft = Math.max(0, Math.min(left + deltaX, left + width - 30));
  const newStartMinutes = Math.round(((newLeft / containerWidth) * visibleMinutes + rangeStartMinutes) / 15) * 15;
        const currentEndMinutes = timeToMinutes(currentEndTime);
        const maxStartMinutes = Math.max(rangeStartMinutes, currentEndMinutes - 15);
        const constrainedStartMinutes = Math.min(newStartMinutes, maxStartMinutes);
        newStartTime = `${String(Math.floor(constrainedStartMinutes / 60)).padStart(2,'0')}:${String(constrainedStartMinutes % 60).padStart(2,'0')}`;
      } else {
  const newWidth = Math.max(30, width + deltaX);
  const newRight = left + newWidth;
  const newEndMinutes = Math.round(((newRight / containerWidth) * visibleMinutes + rangeStartMinutes) / 15) * 15;
        const currentStartMinutes = timeToMinutes(currentStartTime);
        const minEndMinutes = currentStartMinutes + 15;
        const maxEndMinutes = rangeEndMinutes;
        const constrainedEndMinutes = Math.min(Math.max(newEndMinutes, minEndMinutes), maxEndMinutes);
        newEndTime = `${String(Math.floor(constrainedEndMinutes / 60)).padStart(2,'0')}:${String(constrainedEndMinutes % 60).padStart(2,'0')}`;
      }
      
      // Update optimistic times for immediate visual feedback (throttled)
      const now = Date.now();
      if (now - lastUpdateTime > 16) { // ~60fps throttling
        setOptimisticTimes({ start: newStartTime, end: newEndTime });
        setLastUpdateTime(now);
      }
      
      // Set drag preview for real-time time display
      setDragPreview({
        start: newStartTime,
        end: newEndTime,
        type: handle === 'start' ? 'resize-start' : 'resize-end',
        mouseX: e.clientX,
        mouseY: e.clientY
      });
    };
    
    const handleMouseUp = (e) => {
      setIsResizing(false);
      setResizeHandle(null);
      setDragPreview(null); // Clear drag preview
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Calculate final times
      const deltaX = e.clientX - startX;
      let newStartTime = currentStartTime;
      let newEndTime = currentEndTime;
      
  if (handle === 'start') {
  const newLeft = Math.max(0, Math.min(left + deltaX, left + width - 30));
  const newStartMinutes = Math.round(((newLeft / containerWidth) * visibleMinutes + rangeStartMinutes) / 15) * 15;
        const currentEndMinutes = timeToMinutes(currentEndTime);
        const maxStartMinutes = Math.max(rangeStartMinutes, currentEndMinutes - 15);
        const constrainedStartMinutes = Math.min(newStartMinutes, maxStartMinutes);
        newStartTime = `${String(Math.floor(constrainedStartMinutes / 60)).padStart(2,'0')}:${String(constrainedStartMinutes % 60).padStart(2,'0')}`;
      } else {
        const newWidth = Math.max(30, width + deltaX);
        const newRight = left + newWidth;
  const newEndMinutes = Math.round(((newRight / containerWidth) * visibleMinutes + rangeStartMinutes) / 15) * 15;
        const currentStartMinutes = timeToMinutes(currentStartTime);
        const minEndMinutes = currentStartMinutes + 15;
        const maxEndMinutes = rangeEndMinutes;
        const constrainedEndMinutes = Math.min(Math.max(newEndMinutes, minEndMinutes), maxEndMinutes);
        newEndTime = `${String(Math.floor(constrainedEndMinutes / 60)).padStart(2,'0')}:${String(constrainedEndMinutes % 60).padStart(2,'0')}`;
      }
      
      // Keep optimistic times and save to database
      if (newStartTime !== shift.shiftStartTime || newEndTime !== shift.shiftEndTime) {
        setIsUpdating(true);
        Promise.resolve(onShiftResize?.(shift, newStartTime, newEndTime)).then(() => {
          // Clear optimistic state after database update with minimal delay
          setTimeout(() => {
            setOptimisticTimes(null);
            setIsUpdating(false);
          }, 50);
        }).catch(() => {
          // Revert on error
          setOptimisticTimes(null);
          setIsUpdating(false);
        });
      } else {
        // No change, clear optimistic state immediately
        setOptimisticTimes(null);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [currentStartTime, currentEndTime, left, width, shift, onShiftResize, visibleMinutes, rangeStartMinutes, rangeEndMinutes, lastUpdateTime, containerWidth]);

  // Handle dragging the entire shift to move it earlier/later
  const handleShiftDragStart = useCallback((e) => {
    // Don't start drag if clicking on resize handles
    if (e.target.classList.contains('resize-handle') || e.target.closest('.resize-handle')) {
      return;
    }
    
    if (isResizing) return; // Don't allow time dragging while resizing
    
    e.stopPropagation();
    e.preventDefault();
    setIsDraggingTime(true);
  // inform parent that this shift drag has started
  try { onDragStartProp?.(shift.id); } catch (err) { /* ignore */ }
    
    const startX = e.clientX;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      
      // Calculate new times based on drag distance
  const deltaMinutes = Math.round((deltaX / containerWidth) * visibleMinutes / 15) * 15;
      
      const originalStartMinutes = timeToMinutes(shift.shiftStartTime);
      const originalEndMinutes = timeToMinutes(shift.shiftEndTime);
      const shiftDuration = originalEndMinutes - originalStartMinutes;
      
      let newStartMinutes = originalStartMinutes + deltaMinutes;
      let newEndMinutes = originalEndMinutes + deltaMinutes;
      
      // Keep within visible range bounds
      newStartMinutes = Math.max(rangeStartMinutes, Math.min(newStartMinutes, rangeEndMinutes - shiftDuration));
      newEndMinutes = newStartMinutes + shiftDuration;
      
      const newStartTime = `${String(Math.floor(newStartMinutes / 60)).padStart(2,'0')}:${String(newStartMinutes % 60).padStart(2,'0')}`;
      const newEndTime = `${String(Math.floor(newEndMinutes / 60)).padStart(2,'0')}:${String(newEndMinutes % 60).padStart(2,'0')}`;
      
      // Update optimistic times for immediate feedback (throttled)
      const now = Date.now();
      if (now - lastUpdateTime > 16) { // ~60fps throttling
        setOptimisticTimes({ start: newStartTime, end: newEndTime });
        setLastUpdateTime(now);
      }
      
      // Set drag preview for real-time time display
      setDragPreview({
        start: newStartTime,
        end: newEndTime,
        type: 'move',
        mouseX: e.clientX,
        mouseY: e.clientY
      });
    };
    
    const handleMouseUp = (e) => {
      setIsDraggingTime(false);
      setDragPreview(null); // Clear drag preview
  // inform parent that this shift drag has ended
  try { onDragEndProp?.(shift.id); } catch (err) { /* ignore */ }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Calculate final times using the current mouse position
      const deltaX = e.clientX - startX;
  const deltaMinutes = Math.round((deltaX / containerWidth) * visibleMinutes / 15) * 15;
      
      const originalStartMinutes = timeToMinutes(shift.shiftStartTime);
      const originalEndMinutes = timeToMinutes(shift.shiftEndTime);
      const shiftDuration = originalEndMinutes - originalStartMinutes;
      
      let newStartMinutes = originalStartMinutes + deltaMinutes;
      let newEndMinutes = originalEndMinutes + deltaMinutes;
      
      // Keep within visible range bounds
      newStartMinutes = Math.max(rangeStartMinutes, Math.min(newStartMinutes, rangeEndMinutes - shiftDuration));
      newEndMinutes = newStartMinutes + shiftDuration;
      
      const newStartTime = `${String(Math.floor(newStartMinutes / 60)).padStart(2,'0')}:${String(newStartMinutes % 60).padStart(2,'0')}`;
      const newEndTime = `${String(Math.floor(newEndMinutes / 60)).padStart(2,'0')}:${String(newEndMinutes % 60).padStart(2,'0')}`;
      
      // Only save if times actually changed AND not resizing AND we moved more than threshold AND not already updating
      if ((newStartTime !== shift.shiftStartTime || newEndTime !== shift.shiftEndTime) && 
          !isResizing && 
          Math.abs(deltaMinutes) >= 15 && // Minimum 15-minute threshold to avoid accidental moves
          !updateInProgress) { // Prevent concurrent updates
        
        setIsUpdating(true);
        setUpdateInProgress(true);
        
        Promise.resolve(onShiftResize?.(shift, newStartTime, newEndTime)).then(() => {
          // Clear optimistic state after successful update with minimal delay
          setTimeout(() => {
            setOptimisticTimes(null);
            setIsUpdating(false);
            setUpdateInProgress(false);
          }, 50);
        }).catch((err) => {
          console.error("Timeline drag update failed:", err);
          // Revert on error
          setOptimisticTimes(null);
          setIsUpdating(false);
          setUpdateInProgress(false);
        });
      } else {
        // Skip update - drag threshold not met or already updating
        // No change or was resizing or movement too small or update in progress, clear optimistic state immediately
        setOptimisticTimes(null);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [shift, isResizing, onShiftResize, visibleMinutes, rangeStartMinutes, rangeEndMinutes, lastUpdateTime, updateInProgress, containerWidth, onDragStartProp, onDragEndProp]);

  // Don't render if shift is not visible in current range
  if (!isVisible) {
    return null;
  }
  // Compute inner shift height to fit within the ROW_HEIGHT and center vertically
  // Allow parent to override inner shift height (used for lane rendering)
  const computedShiftInnerHeight = Math.max(24, ROW_HEIGHT - 12);
  const effectiveShiftInnerHeight = shiftInnerHeightProp || computedShiftInnerHeight;
  const shiftTopOffset = Math.round((ROW_HEIGHT - effectiveShiftInnerHeight) / 2);

  // Compute a deterministic sx object for the shift so styling doesn't accidentally
  // carry over from multiple conditional spreads. Precedence:
  // - If the shift is unpublished (isDraftNew) it gets the yellow draft background.
  // - If the shift is outside availability (isOutsideAvailability) it gets a red solid border.
  // - Otherwise use normal role/roleColor-based styling.
  const showDraftBg = isDraftNew || isDraggingTime; // published shifts turn yellow while dragging
  const computedSx = {
    borderRadius: 2,
    boxSizing: 'border-box',
    // background: draft overrides default color but may be overridden below for outside-availability
  // draft/during-drag yellow for better contrast
  bgcolor: showDraftBg ? ((t) => t.palette.mode === 'dark' ? '#FFD54F' : '#FFEB3B') : shiftColor,
    color: showDraftBg ? 'text.primary' : 'white',
    textAlign: 'left',
    boxShadow: isDragOverlay ? 4 : '0 2px 4px rgba(0,0,0,0.1)',
    cursor: isResizing ? (resizeHandle === 'start' ? 'w-resize' : 'e-resize') : (isDraggingTime ? 'grabbing' : 'grab'),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingLeft: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
    overflow: 'hidden',
    transition: isResizing || isDraggingTime ? 'none' : 'all 0.2s ease-in-out',
    transform: `translateZ(0)`,
    backgroundImage: showDraftBg
      ? 'none'
      : (isUpdating
          ? `linear-gradient(135deg, ${shiftColor} 0%, ${shiftColor}AA 50%, ${shiftColor} 100%)`
          : isOpenShift || shift.isOpenShift
            ? `repeating-linear-gradient(45deg, ${shiftColor}, ${shiftColor} 10px, ${shiftColor}DD 10px, ${shiftColor}DD 20px)`
            : `linear-gradient(135deg, ${shiftColor} 0%, ${shiftColor}CC 100%)`),
    outline: isResizing || isDraggingTime ? '2px dashed rgba(255,255,255,0.8)' : 'none',
    outlineOffset: isResizing || isDraggingTime ? '2px' : '0',
    userSelect: 'none',
    pointerEvents: 'auto',
    opacity: isUpdating ? 0.8 : (isPendingDelete ? 0.6 : 1),
    '&:active': { cursor: isResizing ? (resizeHandle === 'start' ? 'w-resize' : 'e-resize') : 'grabbing' },
    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.15)', transform: isResizing || isDraggingTime ? 'none' : 'translateY(-1px)', '& .resize-handle': { opacity: 1 }},
  };

  // border handling: default depends on draft or normal; outside-availability always forces red solid border
    if (isOutsideAvailability) {
    // force error border; published outside keeps role color unless it's being dragged (then keep yellow)
    computedSx.border = (t) => `2px solid ${t.palette.error.main}`;
    if (!isDraftNew && !isDraggingTime) {
      // published but outside availability and not dragging: keep existing bg but show red border
      computedSx.bgcolor = shiftColor;
    } else {
      // draft & outside OR dragging published shift: keep the draft yellow background but show the error border
      computedSx.bgcolor = (t) => t.palette.mode === 'dark' ? '#FFD54F' : '#FFEB3B';
    }
  } else {
    // not outside availability: drafts should NOT show a dashed warning border.
    // Keep drafts as yellow background with no border; only published shifts keep the role border.
    computedSx.border = isDraftNew ? 'none' : `2px ${shiftBorderStyle} ${shiftColor}`;
  }

  return (
    <>
      {/* Real-time drag preview tooltip */}
      {dragPreview && (
        <Box
          sx={{
            position: 'fixed',
            left: `${Math.min(dragPreview.mouseX + 10, window.innerWidth - 150)}px`,
            top: `${Math.max(dragPreview.mouseY - 40, 10)}px`,
            zIndex: 9999,
            bgcolor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: 600,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {dragPreview.type === 'resize-start' && (
            <span>Start: {formatTime(dragPreview.start)}</span>
          )}
          {dragPreview.type === 'resize-end' && (
            <span>End: {formatTime(dragPreview.end)}</span>
          )}
          {dragPreview.type === 'move' && (
            <span>{formatTime(dragPreview.start)} – {formatTime(dragPreview.end)}</span>
          )}
        </Box>
      )}

      {/* Show tooltip when outside availability */}
      {isOutsideAvailability ? (
        <Tooltip title="Outside employee availability" arrow placement="top">
          <Box
            data-shift-id={shift.id}
            role="button"
            tabIndex={0}
            aria-label={`${roleName} shift from ${formatTime(currentStartTime)} to ${formatTime(currentEndTime)}. Drag to move, drag edges to resize.`}
            style={{
              position: 'absolute',
              left: `${left}px`,
              width: `${width}px`,
              top: `${topOffset !== null ? topOffset : shiftTopOffset}px`,
              height: `${effectiveShiftInnerHeight}px`,
              zIndex: isDraggingTime || isResizing ? 1000 : 1,
            }}
            onMouseDown={(e) => { mouseDownRef.current = { x: e.clientX, y: e.clientY, time: Date.now() }; movedRef.current = false; handleShiftDragStart?.(e); }}
            onMouseUp={(e) => {
              // treat as click when there was no movement
              const md = mouseDownRef.current;
              const dx = Math.abs((e.clientX || 0) - (md?.x || 0));
              const dy = Math.abs((e.clientY || 0) - (md?.y || 0));
              const dt = Date.now() - (md?.time || 0);
              if (!movedRef.current && dx < 6 && dy < 6 && dt < 500) {
                // synthetic event for context menu
                const synthetic = { ...e, preventDefault: () => {}, stopPropagation: () => {} };
                onShiftContextMenu?.(synthetic, shift);
              }
              mouseDownRef.current = null; movedRef.current = false;
            }}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onShiftContextMenu?.(e, shift); }}
            onTouchStart={(e) => { const t = e.touches && e.touches[0]; if (t) { mouseDownRef.current = { x: t.clientX, y: t.clientY, time: Date.now() }; movedRef.current = false; } handleShiftDragStart?.(e.touches ? e.touches[0] : e); }}
            onTouchMove={(e) => { movedRef.current = true; }}
            onTouchEnd={(e) => { const t = e.changedTouches && e.changedTouches[0]; const ev = t || e; const md = mouseDownRef.current; const dx = Math.abs((ev.clientX || 0) - (md?.x || 0)); const dy = Math.abs((ev.clientY || 0) - (md?.y || 0)); const dt = Date.now() - (md?.time || 0); if (!movedRef.current && dx < 6 && dy < 6 && dt < 500) { onShiftContextMenu?.(ev, shift); } mouseDownRef.current = null; movedRef.current = false; }}
            sx={computedSx}
          >
            {/* Left resize handle */}
            <Box
              className="resize-handle"
              onMouseDown={handleResizeStart('start')}
              sx={{ position: 'absolute', left: '-2px', top: 0, bottom: 0, width: '8px', cursor: 'w-resize', bgcolor: 'rgba(255,255,255,0.6)', borderRight: '2px solid rgba(255,255,255,0.8)', borderRadius: '4px 0 0 4px', opacity: 0.7, transition: 'opacity 0.2s', zIndex: 10, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.8)' } }}
            />

            {/* Shift content */}
            <Typography variant="caption" sx={{ color: isDraftNew ? 'text.primary' : 'white', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1, pointerEvents: 'none', textDecoration: isPendingDelete ? 'line-through' : 'none' }}>
              {formatTime(currentStartTime)} – {formatTime(currentEndTime)}
            </Typography>
            <Typography variant="caption" sx={{ color: isDraftNew ? 'text.secondary' : 'rgba(255,255,255,0.9)', fontSize: '0.65rem', lineHeight: 1, pointerEvents: 'none', textDecoration: isPendingDelete ? 'line-through' : 'none' }}>
              {isOpenShift || shift.isOpenShift ? "🔓 OPEN" : roleName}
            </Typography>

            {/* Break indicators */}
            {shift.breaks && shift.breaks.map((breakItem, index) => {
              const originalStart = timeToMinutes(shift.shiftStartTime);
              const originalEnd = timeToMinutes(shift.shiftEndTime);
              const originalDuration = originalEnd - originalStart;
              const bStart = timeToMinutes(breakItem.startTime);
              const bDur = breakItem.duration || 30;
              if (bStart >= originalStart && (bStart + bDur) <= originalEnd) {
                const startPct = ((bStart - originalStart) / originalDuration) * 100;
                const widthPct = (bDur / originalDuration) * 100;
                return (
                  <Box key={`break-${index}-${currentStartTime}-${currentEndTime}`} sx={{ position: 'absolute', left: `${Math.max(0, startPct)}%`, width: `${Math.min(widthPct, 100 - Math.max(0, startPct))}%`, top: 0, bottom: 0, bgcolor: 'rgba(128,128,128,0.7)', borderLeft: '1px solid rgba(255,255,255,0.3)', borderRight: '1px solid rgba(255,255,255,0.3)', pointerEvents: 'none', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 500, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                      {breakItem.isPaid ? 'P' : 'U'}
                    </Typography>
                  </Box>
                );
              }
              return null;
            })}

            {/* Right resize handle */}
            <Box
              className="resize-handle"
              onMouseDown={handleResizeStart('end')}
              sx={{ position: 'absolute', right: '-2px', top: 0, bottom: 0, width: '8px', cursor: 'e-resize', bgcolor: 'rgba(255,255,255,0.6)', borderLeft: '2px solid rgba(255,255,255,0.8)', borderRadius: '0 4px 4px 0', opacity: 0.7, transition: 'opacity 0.2s', zIndex: 10, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.8)' } }}
            />
          </Box>
        </Tooltip>
      ) : (
        <Box
          data-shift-id={shift.id}
          role="button"
          tabIndex={0}
          aria-label={`${roleName} shift from ${formatTime(currentStartTime)} to ${formatTime(currentEndTime)}. Drag to move, drag edges to resize.`}
          style={{
            position: 'absolute',
            left: `${left}px`,
            width: `${width}px`,
            top: `${topOffset !== null ? topOffset : shiftTopOffset}px`,
            height: `${effectiveShiftInnerHeight}px`,
            zIndex: isDraggingTime || isResizing ? 1000 : 1,
          }}
          onMouseDown={(e) => { mouseDownRef.current = { x: e.clientX, y: e.clientY, time: Date.now() }; movedRef.current = false; handleShiftDragStart?.(e); }}
          onMouseUp={(e) => {
            // treat as click when there was no movement
            const md = mouseDownRef.current;
            const dx = Math.abs((e.clientX || 0) - (md?.x || 0));
            const dy = Math.abs((e.clientY || 0) - (md?.y || 0));
            const dt = Date.now() - (md?.time || 0);
            if (!movedRef.current && dx < 6 && dy < 6 && dt < 500) {
              // synthetic event for context menu
              const synthetic = { ...e, preventDefault: () => {}, stopPropagation: () => {} };
              onShiftContextMenu?.(synthetic, shift);
            }
            mouseDownRef.current = null; movedRef.current = false;
          }}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onShiftContextMenu?.(e, shift); }}
          onTouchStart={(e) => { const t = e.touches && e.touches[0]; if (t) { mouseDownRef.current = { x: t.clientX, y: t.clientY, time: Date.now() }; movedRef.current = false; } handleShiftDragStart?.(e.touches ? e.touches[0] : e); }}
          onTouchMove={(e) => { movedRef.current = true; }}
          onTouchEnd={(e) => { const t = e.changedTouches && e.changedTouches[0]; const ev = t || e; const md = mouseDownRef.current; const dx = Math.abs((ev.clientX || 0) - (md?.x || 0)); const dy = Math.abs((ev.clientY || 0) - (md?.y || 0)); const dt = Date.now() - (md?.time || 0); if (!movedRef.current && dx < 6 && dy < 6 && dt < 500) { onShiftContextMenu?.(ev, shift); } mouseDownRef.current = null; movedRef.current = false; }}
          sx={computedSx}
        >
          {/* Left resize handle */}
          <Box
            className="resize-handle"
            onMouseDown={handleResizeStart('start')}
            sx={{ position: 'absolute', left: '-2px', top: 0, bottom: 0, width: '8px', cursor: 'w-resize', bgcolor: 'rgba(255,255,255,0.6)', borderRight: '2px solid rgba(255,255,255,0.8)', borderRadius: '4px 0 0 4px', opacity: 0.7, transition: 'opacity 0.2s', zIndex: 10, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.8)' } }}
          />

          {/* Shift content */}
          <Typography variant="caption" sx={{ color: isDraftNew ? 'text.primary' : 'white', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1, pointerEvents: 'none', textDecoration: isPendingDelete ? 'line-through' : 'none' }}>
            {formatTime(currentStartTime)} – {formatTime(currentEndTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: isDraftNew ? 'text.secondary' : 'rgba(255,255,255,0.9)', fontSize: '0.65rem', lineHeight: 1, pointerEvents: 'none', textDecoration: isPendingDelete ? 'line-through' : 'none' }}>
            {isOpenShift || shift.isOpenShift ? "🔓 OPEN" : roleName}
          </Typography>

          {/* Break indicators */}
          {shift.breaks && shift.breaks.map((breakItem, index) => {
            const originalStart = timeToMinutes(shift.shiftStartTime);
            const originalEnd = timeToMinutes(shift.shiftEndTime);
            const originalDuration = originalEnd - originalStart;
            const bStart = timeToMinutes(breakItem.startTime);
            const bDur = breakItem.duration || 30;
            if (bStart >= originalStart && (bStart + bDur) <= originalEnd) {
              const startPct = ((bStart - originalStart) / originalDuration) * 100;
              const widthPct = (bDur / originalDuration) * 100;
              return (
                <Box key={`break-${index}-${currentStartTime}-${currentEndTime}`} sx={{ position: 'absolute', left: `${Math.max(0, startPct)}%`, width: `${Math.min(widthPct, 100 - Math.max(0, startPct))}%`, top: 0, bottom: 0, bgcolor: 'rgba(128,128,128,0.7)', borderLeft: '1px solid rgba(255,255,255,0.3)', borderRight: '1px solid rgba(255,255,255,0.3)', pointerEvents: 'none', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 500, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                    {breakItem.isPaid ? 'P' : 'U'}
                  </Typography>
                </Box>
              );
            }
            return null;
          })}

          {/* Right resize handle */}
          <Box
            className="resize-handle"
            onMouseDown={handleResizeStart('end')}
            sx={{ position: 'absolute', right: '-2px', top: 0, bottom: 0, width: '8px', cursor: 'e-resize', bgcolor: 'rgba(255,255,255,0.6)', borderLeft: '2px solid rgba(255,255,255,0.8)', borderRadius: '0 4px 4px 0', opacity: 0.7, transition: 'opacity 0.2s', zIndex: 10, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.8)' } }}
          />
          {/* debug badge removed */}
        </Box>
      )}
    </>
  );
}

// Timeline row (non-droppable, just a container for timeline shifts)
function DroppableTimelineRow({ 
  employee, 
  dept,
  shifts, 
  roleMap, 
  onContextMenu, 
  date, 
  onShiftResize,
  visibleMinutes,
  rangeStartMinutes,
  rangeEndMinutes,
  containerWidth = TIMELINE_WIDTH,
  layoutTick = 0,
  parentLaneLayout = null,
  isOpenShifts = false,
  isWeekPublished = false,
  availability = null,
}) {
  const uniqueShifts = useMemo(() => {
    const map = new Map();
    (shifts || []).forEach(s => { if (s?.id) map.set(s.id, s); });
    return Array.from(map.values());
  }, [shifts]);

  // Compute stacking lanes and required row height
  const laneLayout = useMemo(() => {
    const intervals = (uniqueShifts || []).map(s => {
      let start = 0; let end = 0;
      try {
        start = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
        end = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
        if (end === 0) end = 24 * 60;
      } catch (e) { }
      return { id: s.id, start, end };
    }).sort((a,b) => a.start - b.start || (a.id > b.id ? 1 : -1));

    const lanes = [];
    const laneMap = new Map();
    intervals.forEach(iv => {
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        const last = lanes[i][lanes[i].length - 1];
        if (!last || last.end <= iv.start) {
          lanes[i].push(iv);
          laneMap.set(iv.id, i);
          placed = true;
          break;
        }
      }
      if (!placed) {
        lanes.push([iv]);
        laneMap.set(iv.id, lanes.length - 1);
      }
    });

    const laneCount = Math.max(1, lanes.length);
    const shiftInnerHeight = Math.max(20, ROW_HEIGHT - 12);
    const verticalGap = 6;
    const contentHeight = laneCount * (shiftInnerHeight + verticalGap) - verticalGap;
    const rowHeight = Math.max(ROW_HEIGHT, contentHeight + 8);

    return { laneMap, lanes, laneCount, shiftInnerHeight, verticalGap, rowHeight };
  }, [uniqueShifts]);

  const layout = parentLaneLayout || laneLayout;

  // Render shifts absolutely inside the parent row so the parent row height always contains them
  return (
    <Box
      onContextMenu={(e) => onContextMenu && onContextMenu(e, employee, date, null, dept, null)}
      sx={{ position: 'relative', height: `${layout.rowHeight}px`, borderBottom: '1px solid', borderColor: 'divider', boxSizing: 'border-box' }}
    >
      {uniqueShifts.map(shift => {
        const laneIndex = layout.laneMap.get(shift.id) ?? 0;
        const top = 4 + laneIndex * (layout.shiftInnerHeight + layout.verticalGap);
        return (
          <DraggableTimelineShift
            key={shift.id}
            shift={shift}
            roleMap={roleMap}
            onShiftResize={onShiftResize}
            onShiftContextMenu={(e, shiftData) => onContextMenu && onContextMenu(e, employee, date, shiftData, dept, null)}
            visibleMinutes={visibleMinutes}
            rangeStartMinutes={rangeStartMinutes}
            rangeEndMinutes={rangeEndMinutes}
            containerWidth={containerWidth}
            layoutTick={layoutTick}
            topOffset={top}
            shiftInnerHeight={layout.shiftInnerHeight}
            isOpenShift={isOpenShifts}
            isWeekPublished={isWeekPublished}
            availability={availability}
          />
        );
      })}
    </Box>
  );
}

export default function ScheduleTimelineView({
  byDeptHierarchy,
  weekDays,
  shifts,
  empIdSet,
  analytics,
  openMenu,
  openDialogImmediate,
  // optional external control for list mode
  externalListMode = null,
  onExternalListModeChange = null,
  roles,
  deptNameMap,
  onShiftMove,
  onShiftTimeMove,
  selectedLocation,
  locations = [],
  calculateScheduleConflicts = () => [],
  isWeekPublished = false,
  conflictMode = false,
  focusDate = null,
}) {
  // Debug pointer helper removed to clean console output
  // Load availability for selected location to allow visual availability checks
  const availability = useAvailability(selectedLocation);
  const [selectedDay, setSelectedDay] = useState(0);

  // Allow deep-linking: if URL contains ?date=YYYY-MM-DD prefer that as focus
  useEffect(() => {
    if (!Array.isArray(weekDays) || weekDays.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      const dateParam = params.get('date');
      if (dateParam) {
        const idx = weekDays.findIndex(d => toIsoDateString(d) === dateParam);
        if (idx >= 0) { setSelectedDay(idx); return; }
      }
    } catch (err) { /* ignore */ }
    // fallback to provided focusDate prop if present
    if (!focusDate) return;
    try {
      const dateKey = toIsoDateString(focusDate);
      const idx2 = weekDays.findIndex(d => toIsoDateString(d) === dateKey);
      if (idx2 >= 0) setSelectedDay(idx2);
    } catch (err) { /* ignore malformed dates */ }
  }, [focusDate, weekDays]);
  // Removed: inline Add Employee button in timeline view
  // grouping state
  const [quickViewEmployee, setQuickViewEmployee] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [groupByRole, setGroupByRole] = useState(false);
  const [internalListMode, setInternalListMode] = useState('group'); // 'group' | 'byEmployee' | 'byStartTime'
  const listMode = externalListMode !== null ? externalListMode : internalListMode;
  const setListMode = (v) => {
    if (onExternalListModeChange) return onExternalListModeChange(v);
    return setInternalListMode(v);
  };

  // Keep groupByRole in sync when listMode is changed externally/ internally
  useEffect(() => {
    if (listMode === 'group') setGroupByRole(true);
    else setGroupByRole(false);
  }, [listMode]);
  const [expandedDepts, setExpandedDepts] = useState(() => {
    const initial = {};
    if (byDeptHierarchy) Object.keys(byDeptHierarchy).forEach(d => initial[d] = true);
    return initial;
  });

  // --- Determine visible time range from selected location scheduling hours ---

  const selectedLocationObj = useMemo(() => {
    if (!selectedLocation || !Array.isArray(locations)) return null;
    return locations.find(l => l.id === selectedLocation) || null;
  }, [locations, selectedLocation]);

  // scheduling hours for the selected day (may be null)
  const schedulingForDay = useMemo(() => {
    if (!selectedLocationObj) return null;
    const dayObj = (weekDays && weekDays[selectedDay]) ? weekDays[selectedDay] : new Date();
    const name = WEEKDAY_NAMES[dayObj.getDay()];
    return (selectedLocationObj.schedulingHours && selectedLocationObj.schedulingHours[name]) || null;
  }, [selectedLocationObj, selectedDay, weekDays]);

  // Base range from scheduling hours or fall back to reasonable defaults (6:00 - 23:00)
  const baseRange = useMemo(() => {
    const defaultStart = 6 * 60;
    const defaultEnd = 23 * 60;
    if (!schedulingForDay) return { start: defaultStart, end: defaultEnd };
    try {
      let s = schedulingForDay.start ? timeToMinutes(schedulingForDay.start) : defaultStart;
      let e = schedulingForDay.end ? timeToMinutes(schedulingForDay.end) : defaultEnd;
      // treat midnight 00:00 as end of day (1440)
      if (e === 0) e = 24 * 60;
      // clamp
      s = Math.max(0, Math.min(24 * 60, s));
      e = Math.max(0, Math.min(24 * 60, e));
      // if end <= start, assume end is end of day
      if (e <= s) e = Math.max(e, s + 60);
      return { start: s, end: e };
    } catch (err) {
      return { start: defaultStart, end: defaultEnd };
    }
  }, [schedulingForDay]);

  // NOTE: range is computed below after dayShifts is available

  // Helper: compute lane layout for a set of shifts (same algorithm used inside DroppableTimelineRow)
  const computeLaneLayoutForShifts = useCallback((shiftsList) => {
    const unique = (shiftsList || []).slice();
    const intervals = unique.map(s => {
      let start = 0; let end = 0;
      try {
        start = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
        end = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
        if (end === 0) end = 24 * 60;
      } catch (e) { }
      return { id: s.id, start, end };
    }).sort((a,b) => a.start - b.start);

    const lanes = [];
    const laneMap = new Map();
    intervals.forEach(iv => {
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        const last = lanes[i][lanes[i].length - 1];
        if (!last || last.end <= iv.start) {
          lanes[i].push(iv);
          laneMap.set(iv.id, i);
          placed = true;
          break;
        }
      }
      if (!placed) {
        lanes.push([iv]);
        laneMap.set(iv.id, lanes.length - 1);
      }
    });

    const laneCount = Math.max(1, lanes.length);
    const shiftInnerHeight = Math.max(20, ROW_HEIGHT - 12);
    const verticalGap = 6;
    const contentHeight = laneCount * (shiftInnerHeight + verticalGap) - verticalGap;
    const rowHeight = Math.max(ROW_HEIGHT, contentHeight + 8);

    return { laneMap, lanes, laneCount, shiftInnerHeight, verticalGap, rowHeight };
  }, []);

  const roleMap = useMemo(() => {
    const map = {};
    (roles || []).forEach(r => { map[r.id] = { name: r.name, color: r.color || "#888", archived: !!r.archived }; });
    map["unassigned"] = { name: "Unassigned", color: "#666", archived: false };
    return map;
  }, [roles]);

  // Prefer flattened assignments subcollection when available (assignmentsMap). Build a lookup
  // of employee objects from the incoming byDeptHierarchy so we can map ids -> employee records.
  const { map: assignmentsMap } = useAssignmentsForLocation(selectedLocation);

  const employeeLookup = useMemo(() => {
    const lookup = {};
    if (!byDeptHierarchy) return lookup;
    Object.values(byDeptHierarchy).forEach(roleGroups => {
      Object.values(roleGroups).flat().forEach(emp => { if (emp && emp.id) lookup[emp.id] = emp; });
    });
    return lookup;
  }, [byDeptHierarchy]);

  const employeesByDept = useMemo(() => {
    // If flattened assignments are present for this location, build structure from them.
    if (assignmentsMap && Object.keys(assignmentsMap).length > 0) {
      const structure = {};
      Object.entries(assignmentsMap).forEach(([empId, locSlice]) => {
  const hasEmployeeDoc = !!employeeLookup[empId];
  // If we can't resolve an employee record, don't render a placeholder row.
  // This prevents showing raw Firestore IDs (common for terminated/hidden employees).
  if (!hasEmployeeDoc) return;

  const emp = employeeLookup[empId];
  // Filter out terminated/inactive employees.
  if (isTerminatedEmployee(emp)) return;
        const departments = (locSlice && locSlice.departments) || {};
        Object.entries(departments).forEach(([deptId, deptData]) => {
          const rolesObj = deptData.roles || {};
          // rolesObj may be array (legacy) or object
          let roleIds = [];
          if (Array.isArray(rolesObj)) {
            roleIds = rolesObj.slice();
          } else {
            roleIds = Object.keys(rolesObj).filter(rid => {
              const v = rolesObj[rid];
              return v === true || (v && (v.assigned === true || v.isPrimary === true));
            });
          }
          if (!structure[deptId]) structure[deptId] = {};
          roleIds.forEach(roleId => {
            if (!structure[deptId][roleId]) structure[deptId][roleId] = [];
            // avoid duplicates
            if (!structure[deptId][roleId].some(e => e.id === emp.id)) structure[deptId][roleId].push(emp);
          });
        });
      });
      // sort each role list by name
      Object.values(structure).forEach(roleGroups => {
        Object.keys(roleGroups).forEach(rid => {
          roleGroups[rid].sort((a,b) => (a.name || '').localeCompare(b.name || ''));
        });
      });
      return structure;
    }

    // Fallback: build from provided byDeptHierarchy
    const structure = {};
    if (!byDeptHierarchy || Object.keys(byDeptHierarchy).length === 0) return structure;
    Object.entries(byDeptHierarchy).forEach(([deptId, roleGroups]) => {
      structure[deptId] = {};
      Object.entries(roleGroups).forEach(([roleId, emps]) => {
        if (emps && emps.length > 0) {
          structure[deptId][roleId] = emps
            .filter(e => !isTerminatedEmployee(e))
            .slice()
            .sort((a,b) => {
            const nameA = a.name || [a.firstName, a.lastName].filter(Boolean).join(" ");
            const nameB = b.name || [b.firstName, b.lastName].filter(Boolean).join(" ");
            return nameA.localeCompare(nameB);
          });
        }
      });
    });
    return structure;
  }, [assignmentsMap, byDeptHierarchy, employeeLookup]);

  // departmentsForRender will be computed after dayShifts is known (see below)

  const effectiveShifts = useMemo(() => {
    const byId = new Map();
    (shifts || []).forEach(s => {
      if (!s || s.deleted) return;
      const eff = isWeekPublished && s.pendingUpdate && Object.keys(s.pendingUpdate).length > 0 ? { ...s, ...s.pendingUpdate } : s;
      byId.set(s.id, eff);
    });
    return Array.from(byId.values());
  }, [shifts, isWeekPublished]);

  const selectedDate = weekDays[selectedDay];
  const dayKey = toIsoDateString(selectedDate);
  const dayShifts = useMemo(() => {
    return effectiveShifts.filter(s => {
      const sd = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
      return toIsoDateString(sd) === dayKey;
    });
  }, [effectiveShifts, dayKey]);

  // Prepare departments structure for rendering based on listMode
  const departmentsForRender = useMemo(() => {
    if (listMode === 'group') return employeesByDept;
    // Flatten to a single list per department and sort according to mode
    const out = {};
    Object.entries(employeesByDept).forEach(([deptId, roleGroups]) => {
      const flattened = Object.values(roleGroups).flat();
      // dedupe
      const allEmployees = Array.from(new Map((flattened || []).map(e => [e.id, e])).values());
      if (listMode === 'byEmployee') {
        out[deptId] = { 'all': allEmployees.slice().sort((a,b) => {
          const nameA = a.name || [a.firstName, a.lastName].filter(Boolean).join(' ');
          const nameB = b.name || [b.firstName, b.lastName].filter(Boolean).join(' ');
          return nameA.localeCompare(nameB);
        }) };
      } else if (listMode === 'byStartTime') {
        // compute earliest shift start for each employee for the selected day
        const withStart = allEmployees.map(emp => {
          const empShifts = dayShifts.filter(s => s.employeeId === emp.id && s.dept === deptId && !s.isOpenShift);
          const starts = empShifts.map(s => timeToMinutes(s.shiftStartTime || s.startTime || '00:00'));
          const earliest = starts.length ? Math.min(...starts) : Number.MAX_SAFE_INTEGER;
          return { emp, earliest };
        });
        withStart.sort((a,b) => a.earliest - b.earliest || (a.emp.name || (a.emp.firstName + ' ' + a.emp.lastName)).localeCompare(b.emp.name || (b.emp.firstName + ' ' + b.emp.lastName)));
        out[deptId] = { 'all': withStart.map(w => w.emp) };
      }
    });
    return out;
  }, [employeesByDept, listMode, dayShifts]);

  // Measure the timeline container width to avoid gutters and ensure full-bleed behavior
  const timelineRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(TIMELINE_WIDTH);
  useEffect(() => {
    if (!timelineRef.current) return;
    const el = timelineRef.current;
    const ro = new ResizeObserver(entries => {
      for (let entry of entries) {
        const w = entry.contentRect.width || TIMELINE_WIDTH;
        setContainerWidth(Math.max(300, Math.round(w)));
      }
    });
    ro.observe(el);
    setContainerWidth(Math.max(300, Math.round(el.getBoundingClientRect().width)));
    return () => ro.disconnect();
  }, []);

  // Trigger re-layout when the browser zoom level or window size changes.
  // Some browsers don't change containerWidth on zoom; use a small layoutTick state
  // updated on resize and ctrl+wheel (zoom) so shift position memos recompute.
  const [layoutTick, setLayoutTick] = useState(0);
  // Hover cell key (kept for future features)
  // Key format: `${deptId}|${roleId||'all'}|${employeeId===null?'open':employeeId}|${index}`
  const [hoverCellKey, setHoverCellKey] = useState(null);
  useEffect(() => {
    let timeout = null;
    const handler = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setLayoutTick(t => t + 1), 120);
    };
    const wheelHandler = (e) => { if (e.ctrlKey) handler(); };
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    window.addEventListener('wheel', wheelHandler, { passive: true });
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
      window.removeEventListener('wheel', wheelHandler);
    };
  }, []);

  // Auto-expand rule: if any shift for the selected day falls outside scheduling hours,
  // extend the visible range to include them
  const { rangeStartMinutes, rangeEndMinutes, visibleMinutes } = useMemo(() => {
    const dayShiftsTimes = (dayShifts || []).reduce((acc, s) => {
      try {
        const start = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
        let end = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
        if (end === 0) end = 24 * 60;
        acc.push({ start, end });
      } catch (e) { /* ignore parsing errors */ }
      return acc;
    }, []);

    let start = baseRange.start;
    let end = baseRange.end;

    dayShiftsTimes.forEach(t => {
      if (t.start < start) start = t.start;
      if (t.end > end) end = t.end;
    });

    // snap to 15-minute grid and clamp
    start = Math.max(0, Math.floor(start / 15) * 15);
    end = Math.min(24 * 60, Math.ceil(end / 15) * 15);
    const visible = Math.max(15, end - start);
    return { rangeStartMinutes: start, rangeEndMinutes: end, visibleMinutes: visible };
  }, [baseRange, dayShifts]);

  // (debug logs removed)

  // Hour-only markers (label each hour). We still render shifts snapped to 15 minute positions.
  const timeMarkers = useMemo(() => {
    const startHour = Math.floor(rangeStartMinutes / 60);
    const endHour = Math.ceil(rangeEndMinutes / 60);
    const markers = [];
    // use < endHour to avoid emitting a marker for the end boundary which can create a tiny overflow
    for (let h = startHour; h < endHour; h++) {
      const mins = h * 60;
      markers.push({ time: `${String(h).padStart(2,'0')}:00`, label: `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`, minutes: mins });
    }
    return markers;
  }, [rangeStartMinutes, rangeEndMinutes]);

  // Grid markers at hours (positions). No sub-hour ticks.
  const gridMarkers = useMemo(() => {
    const markers = [];
    const startHour = Math.floor(rangeStartMinutes / 60);
    const endHour = Math.ceil(rangeEndMinutes / 60);
    const hours = Math.max(1, endHour - startHour);
    for (let i = 0; i < hours; i++) {
      const h = startHour + i;
      markers.push(getTimelinePosition(`${String(h).padStart(2,'0')}:00`, visibleMinutes, containerWidth, rangeStartMinutes));
    }
    return markers;
  }, [rangeStartMinutes, rangeEndMinutes, visibleMinutes, containerWidth]);

  // Compute unique employee counts per hour for the current day and visible range
  const hourlyEmployeeCounts = useMemo(() => {
    const map = {};
    if (!dayShifts || dayShifts.length === 0) return map;
    dayShifts.forEach(s => {
      try {
        const start = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
        let end = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
        if (end === 0) end = 24 * 60;
        const emp = s.employeeId;
        if (!emp) return; // skip open shifts / unassigned
        // clamp to visible range
        const clampedStart = Math.max(start, rangeStartMinutes);
        const clampedEnd = Math.min(end, rangeEndMinutes);
        if (clampedEnd <= clampedStart) return;
        const startHour = Math.floor(clampedStart / 60);
        const endHour = Math.floor((clampedEnd - 1) / 60);
        for (let h = startHour; h <= endHour; h++) {
          const key = h * 60;
          if (!map[key]) map[key] = new Set();
          map[key].add(emp);
        }
      } catch (e) { /* ignore parse errors */ }
    });
    const counts = {};
    Object.keys(map).forEach(k => { counts[Number(k)] = map[k].size; });
    return counts;
  }, [dayShifts, rangeStartMinutes, rangeEndMinutes]);

  const handleShiftResize = useCallback((shift, newStartTime, newEndTime) => {
    return Promise.resolve(onShiftTimeMove?.(shift, newStartTime, newEndTime));
  }, [onShiftTimeMove]);

  // Width in pixels of one hour cell in the current visible range
  const hourWidth = useMemo(() => {
    if (!visibleMinutes || visibleMinutes <= 0) return containerWidth;
    return (containerWidth * 60) / visibleMinutes;
  }, [containerWidth, visibleMinutes]);

  const toggleDeptExpanded = (dept) => setExpandedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));

  const handleEmployeeQuickView = (employee) => { setQuickViewEmployee(employee); setQuickViewOpen(true); };
  // view menu state
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const openViewMenu = Boolean(viewAnchorEl);
  const handleOpenViewMenu = (e) => setViewAnchorEl(e.currentTarget);
  const handleCloseViewMenu = () => setViewAnchorEl(null);

  return (
    <Box>
      <Paper sx={{ border: 1, borderColor: "grey.300", overflow: 'hidden' }}>
        {/* Day and Time Range Selectors */}
  <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[100] }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
            {weekDays.map((day, index) => (
              <Box key={toIsoDateString(day)} onClick={() => setSelectedDay(index)} sx={{ px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer', bgcolor: selectedDay === index ? 'primary.main' : 'transparent', color: selectedDay === index ? 'primary.contrastText' : 'text.primary', border: 1, borderColor: selectedDay === index ? 'primary.main' : 'divider', transition: 'all 0.15s', '&:hover': { bgcolor: selectedDay === index ? 'primary.dark' : 'action.hover' } }}>
                <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>{day.toLocaleDateString("en-US", { weekday: "short" })}</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem' }}>{day.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {/* View toggles moved into the view menu (icon next to Classic/Smart) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* placeholder - the Menu trigger is rendered next to Classic/Smart buttons below */}
                    </Box>
          </Box>
        </Box>

        {/* Shared timeline viewport: header + rows share the same measured container */}
        <Box sx={{ position: 'relative' }}>
          {/* Left header column (labels gutter). Add Employee button intentionally removed from timeline view. */}
          <Box sx={{ position: 'absolute', left: 0, top: 0, width: `${LABEL_WIDTH}px`, height: HEADER_HEIGHT, borderBottom: 2, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[50], zIndex: 3 }} />
          <Box ref={timelineRef} sx={{ position: 'absolute', left: LABEL_WIDTH, right: 0, top: 0, height: HEADER_HEIGHT, borderBottom: 2, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[50], zIndex: 2 }}>
            {/* 15-minute grid lines */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, height: '100%' }}>
              {gridMarkers.map((position, index) => (
                <Box key={index} sx={{ position: 'absolute', left: `${position}px`, top: 0, bottom: 0, width: '1px', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', zIndex: 0 }} />
              ))}
            </Box>
            {/* Hour markers and labels */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, height: '100%', display: 'flex', alignItems: 'center', zIndex: 3 }}>
              {timeMarkers.map((marker, index) => {
                const leftPos = getTimelinePosition(marker.time, visibleMinutes, containerWidth, rangeStartMinutes);
                return (
                  <Box
                    key={marker.time}
                    sx={{
                      position: 'absolute',
                      left: `${leftPos}px`,
                      width: `${hourWidth}px`,
                      height: '100%',
                      borderLeft: index === 0 ? 'none' : '2px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[50]
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>{marker.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>👤</Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{hourlyEmployeeCounts[marker.minutes] || 0}</Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

          </Box>

          {/* Timeline Content (scrollable) */}
          <Box sx={{ maxHeight: 600, overflow: 'auto', paddingTop: `${HEADER_HEIGHT}px` }}>
          {Object.entries(departmentsForRender).map(([deptId, roleGroups]) => {
            // roleGroups may be either the original role grouping map or { all: [emps...] }
            const flattened = Object.values(roleGroups).flat();
            const allEmployees = Array.from(new Map((flattened || []).map(e => [e.id, e])).values());
            return (
              <Box key={deptId}>
                {/* Department Header */}
                <Box onClick={() => toggleDeptExpanded(deptId)} sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.grey[100], borderBottom: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[600] : t.palette.grey[200] } }}>
                  <IconButton size="small">{expandedDepts[deptId] ? <ExpandLess /> : <ExpandMore />}</IconButton>
                  <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>{deptNameMap && deptNameMap[deptId] ? deptNameMap[deptId] : deptId} ({allEmployees.length})</Typography>
                </Box>

                {/* Department Content */}
                <Collapse in={expandedDepts[deptId]}>
                  {/* When grouping by role, render role sections inside the department */}
                    {groupByRole ? (
                    Object.entries(roleGroups).map(([roleId, emps]) => {
                      const roleOpenShifts = dayShifts.filter(s => s.dept === deptId && s.role === roleId && (s.isOpenShift || !s.employeeId));
                      // Resolve a friendly role label for the header. Prefer the live role document name,
                      // fall back to the denormalized shift.roleName if available, and finally use the raw roleId.
                      // If the role is archived (or missing but the shift carried a name), append " (Archived)".
                      const sampleShift = (dayShifts || []).find(s => s.dept === deptId && s.role === roleId) || null;
                      const roleData = Object.prototype.hasOwnProperty.call(roleMap, roleId) ? roleMap[roleId] : undefined;
                      const headerBase = roleData?.name || sampleShift?.roleName || roleId;
                      // If the role document explicitly marks archived, honor it.
                      // Also treat a missing or empty role doc as archived when we have a sampleShift for that role
                      // — this covers denormalized historical shifts that reference deleted/archived roles.
                      const roleDataIsEmpty = !roleData || (typeof roleData === 'object' && Object.keys(roleData).length === 0);
                      const headerIsArchived = !!roleData?.archived || (roleDataIsEmpty && !!sampleShift) || !!sampleShift?.archived;
                      const headerLabel = headerIsArchived ? `${headerBase} (Archived)` : headerBase;
                      const headerColor = (roleData && Object.keys(roleData).length > 0) ? (roleData.color || null) : null;
                      return (
                        <Box key={`${deptId}-${roleId}`}>
                          {/* Role Header */}
                          <Box
                            sx={(t) => ({
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              bgcolor: headerIsArchived ? (t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[100]) : alpha(headerColor || t.palette.primary.main, 0.12),
                              borderBottom: 1,
                              borderColor: 'divider',
                              position: 'relative',
                              borderLeft: headerIsArchived ? 'none' : `6px solid ${headerColor || t.palette.primary.main}`
                            })}
                          >
                            {/* Active roles: pale background + left accent; archived: gray */}
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                ml: 1,
                                color: 'text.primary'
                              }}
                            >
                              {headerLabel} ({emps.length})
                            </Typography>
                          </Box>

                          {/* Open Shifts Row for this role */}
                          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', position: 'relative', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[850] : t.palette.grey[25] }}>
                            <Box sx={{ width: LABEL_WIDTH, p: 1.5, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[200], zIndex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontStyle: 'italic' }}>📋 Open ({roleOpenShifts.length})</Typography>
                            </Box>
                              <Box sx={{ position: 'absolute', left: LABEL_WIDTH, right: 0, top: 0, bottom: 0, zIndex: 0 }}>
                              {gridMarkers.map((position, index) => (
                                <Box key={index} sx={{ position: 'absolute', left: `${position}px`, top: 0, bottom: 0, width: '1px', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                              ))}
                            </Box>
                            <Box sx={{ flex: 1, position: 'relative', minWidth: containerWidth, zIndex: 1, bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[850] : t.palette.grey[200] }}>
                              {/* per-cell overlays for open shifts row (grouped by role) */}
                              <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 4, pointerEvents: 'none' }}>
                                {(() => {
                                  const occupancy = {};
                                  (roleOpenShifts || []).forEach(s => {
                                    try {
                                      let st = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
                                      let en = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
                                      if (en === 0) en = 24 * 60;
                                      const clampedStart = Math.max(st, rangeStartMinutes);
                                      const clampedEnd = Math.min(en, rangeEndMinutes);
                                      if (clampedEnd <= clampedStart) return;
                                      const startHour = Math.floor(clampedStart / 60);
                                      const endHour = Math.floor((clampedEnd - 1) / 60);
                                      for (let h = startHour; h <= endHour; h++) { occupancy[h * 60] = true; }
                                    } catch (e) { }
                                  });
                                  return gridMarkers.map((position, index) => {
                                    const next = gridMarkers[index + 1] || (position + (containerWidth / (visibleMinutes / 60)));
                                    const width = Math.max(1, Math.round((next - position)));
                                    const hour = Math.floor(rangeStartMinutes / 60) + index;
                                    const markerMinutes = hour * 60;
                                    const isOccupied = !!occupancy[markerMinutes];
                                    const key = `${deptId}|${roleId}|open|${index}`;
                                      return (
                                      <Box
                                        key={`role-open-cell-${index}`}
                                        sx={{ position: 'absolute', left: `${position}px`, top: 0, width: `${width}px`, bottom: 0, pointerEvents: 'none' }}
                                      >
                                        {!isOccupied && (
                                          <>
                                            {/* Non-interactive full-cell layer so shifts receive events */}
                                            <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
                                            {/* Placeholder: interactive hotspot moved to sibling layer so this inert overlay stays non-interactive */}
                                            <Box sx={{ position: 'absolute', left: '50%', top: '8px', transform: 'translate(-50%, 0)', pointerEvents: 'none', width: 40, height: 40 }} />
                                          </>
                                        )}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                              {/* Interactive per-cell hotspots removed — starting fresh per UX request */}
                              {(() => {
                                const openLayout = computeLaneLayoutForShifts(roleOpenShifts);
                                return (
                                  <DroppableTimelineRow
                                    employee={null}
                                    dept={deptId}
                                    shifts={roleOpenShifts}
                                    roleMap={roleMap}
                                    onContextMenu={openMenu}
                                    date={selectedDate}
                                    onShiftResize={handleShiftResize}
                                    visibleMinutes={visibleMinutes}
                                    rangeStartMinutes={rangeStartMinutes}
                                    rangeEndMinutes={rangeEndMinutes}
                                    containerWidth={containerWidth}
                                    layoutTick={layoutTick}
                                    isOpenShifts
                                    isWeekPublished={isWeekPublished}
                                    parentLaneLayout={openLayout}
                                    availability={availability?.availability}
                                  />
                                );
                              })()}
                            </Box>
                          </Box>

                          {/* Employee Rows for this role */}
                          {emps.map(emp => {
                            const empShifts = dayShifts.filter(s => s.employeeId === emp.id && s.dept === deptId && s.role === roleId && !s.isOpenShift);
                            const layout = computeLaneLayoutForShifts(empShifts);
                            return (
                              <Box key={emp.id} sx={{ display: 'flex', /* parent border removed to avoid double gridline */ position: 'relative', height: `${layout.rowHeight}px` }}>
                                <Box sx={{ position: 'absolute', left: LABEL_WIDTH, right: 0, top: 0, bottom: 0, zIndex: 0 }}>
                                  {gridMarkers.map((position, index) => (
                                    <Box key={index} sx={{ position: 'absolute', left: `${position}px`, top: 0, bottom: 0, width: '1px', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                                  ))}
                                </Box>
                                <Box sx={{ width: LABEL_WIDTH, p: 1, borderRight: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', bgcolor: 'background.paper', zIndex: 1, height: `${layout.rowHeight}px`, boxSizing: 'border-box' }}>
                                  <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(" ")}</Typography>
                                      {/* compute total minutes for this employee in visible day range */}
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatMinutesToHours(computeEmployeeWeekMinutes(emp.id, effectiveShifts, weekDays))}</Typography>
                                    </Box>
                                    <Tooltip title="View employee details" arrow>
                                      <IconButton size="small" onClick={() => handleEmployeeQuickView(emp)} sx={{ width: 20, height: 20, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                  <Box sx={{ flex: 1, position: 'relative', minWidth: containerWidth, zIndex: 1, height: `${layout.rowHeight}px`, boxSizing: 'border-box' }}>
                              {/* per-cell overlays */}
                              <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 4, pointerEvents: 'none' }}>
                                {(() => {
                                  // Build occupancy map for this employee row per hour marker
                                  const occupancy = {};
                                  (empShifts || []).forEach(s => {
                                    try {
                                      let st = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
                                      let en = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
                                      if (en === 0) en = 24 * 60;
                                      const clampedStart = Math.max(st, rangeStartMinutes);
                                      const clampedEnd = Math.min(en, rangeEndMinutes);
                                      if (clampedEnd <= clampedStart) return;
                                      const startHour = Math.floor(clampedStart / 60);
                                      const endHour = Math.floor((clampedEnd - 1) / 60);
                                      for (let h = startHour; h <= endHour; h++) { occupancy[h * 60] = true; }
                                    } catch (e) { }
                                  });
                                  return gridMarkers.map((position, index) => {
                                    const next = gridMarkers[index + 1] || (position + (containerWidth / (visibleMinutes / 60)));
                                    const width = Math.max(1, Math.round((next - position)));
                                    const hour = Math.floor(rangeStartMinutes / 60) + index;
                                    const markerMinutes = hour * 60;
                                    const isOccupied = !!occupancy[markerMinutes];
                                    const key = `${deptId}|${roleId}|${emp.id}|${index}`;
                                    return (
                                      <Box
                                        key={`role-emp-cell-${emp.id}-${index}`}
                                        sx={{ position: 'absolute', left: `${position}px`, top: 0, width: `${width}px`, bottom: 0, pointerEvents: 'none' }}
                                      >
                                        {!isOccupied && (
                                          <>
                                            <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
                                            {/* Placeholder: interactive hotspot moved to sibling layer */}
                                            <Box sx={{ position: 'absolute', left: '50%', top: '8px', transform: 'translate(-50%, 0)', pointerEvents: 'none', width: 40, height: 40 }} />
                                          </>
                                        )}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                                  <DroppableTimelineRow
                                    employee={emp}
                                    dept={deptId}
                                    shifts={empShifts}
                                    roleMap={roleMap}
                                    onContextMenu={openMenu}
                                    date={selectedDate}
                                    onShiftResize={handleShiftResize}
                    visibleMinutes={visibleMinutes}
                    rangeStartMinutes={rangeStartMinutes}
                    rangeEndMinutes={rangeEndMinutes}
                    containerWidth={containerWidth}
                                    isWeekPublished={isWeekPublished}
                                    parentLaneLayout={layout}
                                    availability={availability?.availability}
                                  />
                                </Box>
                              {/* Interactive per-cell hotspots removed for role employees */}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })
                  ) : (
                    /* original flat employee view when not grouped by role */
                    <>
                      {/* Open Shifts Row */}
                      {(() => {
                        const openShifts = dayShifts.filter(s => s.dept === deptId && (s.isOpenShift || !s.employeeId));
                        return (
                          <Box sx={{ display: 'flex', /* parent border removed to avoid double gridline */ position: 'relative', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[850] : t.palette.grey[25] }}>
                            {/* Label Column */}
                            <Box sx={{ width: LABEL_WIDTH, p: 1, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.grey[200], zIndex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontStyle: 'italic' }}>📋 Open Shifts ({openShifts.length})</Typography>
                            </Box>
                            {/* Grid lines for row */}
                            <Box sx={{ position: 'absolute', left: LABEL_WIDTH, right: 0, top: 0, bottom: 0, zIndex: 0 }}>
                              {gridMarkers.map((position, index) => (
                                <Box key={index} sx={{ position: 'absolute', left: `${position}px`, top: 0, bottom: 0, width: '1px', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                              ))}
                            </Box>
                            {/* Timeline Row with per-cell overlays */}
                            <Box
                              sx={{ flex: 1, position: 'relative', minWidth: containerWidth, zIndex: 1, bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[850] : t.palette.grey[200] }}
                            >
                              <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, zIndex: 4, pointerEvents: 'none' }}>
                                {(() => {
                                  const occupancy = {};
                                  (openShifts || []).forEach(s => {
                                    try {
                                      let st = timeToMinutes(s.shiftStartTime || s.startTime || '00:00');
                                      let en = timeToMinutes(s.shiftEndTime || s.endTime || '00:00');
                                      if (en === 0) en = 24 * 60;
                                      const clampedStart = Math.max(st, rangeStartMinutes);
                                      const clampedEnd = Math.min(en, rangeEndMinutes);
                                      if (clampedEnd <= clampedStart) return;
                                      const startHour = Math.floor(clampedStart / 60);
                                      const endHour = Math.floor((clampedEnd - 1) / 60);
                                      for (let h = startHour; h <= endHour; h++) { occupancy[h * 60] = true; }
                                    } catch (e) { }
                                  });
                                  return gridMarkers.map((position, index) => {
                                    const next = gridMarkers[index + 1] || (position + (containerWidth / (visibleMinutes / 60)));
                                    const width = Math.max(1, Math.round((next - position)));
                                    const hour = Math.floor(rangeStartMinutes / 60) + index;
                                    const markerMinutes = hour * 60;
                                    const isOccupied = !!occupancy[markerMinutes];
                                    const key = `${deptId}|open|open|${index}`;
                                      return (
                                      <Box
                                        key={`ungrouped-open-cell-${index}`}
                                        sx={{ position: 'absolute', left: `${position}px`, top: 0, width: `${width}px`, bottom: 0, pointerEvents: 'none' }}
                                      >
                                        {!isOccupied && (
                                          <>
                                            <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
                                            {/* Placeholder: interactive hotspot moved to sibling layer */}
                                            <Box sx={{ position: 'absolute', left: '50%', top: '8px', transform: 'translate(-50%, 0)', pointerEvents: 'none', width: 40, height: 40 }} />
                                          </>
                                        )}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                              <DroppableTimelineRow
                                employee={null}
                                dept={deptId}
                                shifts={openShifts}
                                roleMap={roleMap}
                                onContextMenu={openMenu}
                                date={selectedDate}
                                onShiftResize={handleShiftResize}
                                visibleMinutes={visibleMinutes}
                                rangeStartMinutes={rangeStartMinutes}
                                rangeEndMinutes={rangeEndMinutes}
                                containerWidth={containerWidth}
                                isOpenShifts
                                isWeekPublished={isWeekPublished}
                                availability={availability?.availability}
                              />
                              {/* Interactive per-cell hotspots removed for ungrouped open-shifts */}
                            </Box>
                          </Box>
                        );
                      })()}

                      {/* Employee Rows */}
                      {allEmployees.map(emp => {
                        const empShifts = dayShifts.filter(s => s.employeeId === emp.id && s.dept === deptId && !s.isOpenShift);
                        const layout = computeLaneLayoutForShifts(empShifts);
                        return (
                          <Box key={emp.id} sx={{ display: 'flex', /* parent border removed to avoid double gridline */ position: 'relative' }}>
                            {/* Grid lines */}
                            <Box sx={{ position: 'absolute', left: LABEL_WIDTH, right: 0, top: 0, bottom: 0, zIndex: 0 }}>
                              {gridMarkers.map((position, index) => (
                                <Box key={index} sx={{ position: 'absolute', left: `${position}px`, top: 0, bottom: 0, width: '1px', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                              ))}
                            </Box>
                            {/* Name Column */}
                              <Box sx={{ width: LABEL_WIDTH, p: 1, borderRight: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', bgcolor: 'background.paper', zIndex: 1, height: `${layout.rowHeight}px`, boxSizing: 'border-box' }}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(" ")}</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatMinutesToHours(computeEmployeeWeekMinutes(emp.id, effectiveShifts, weekDays))}</Typography>
                                </Box>
                                <Tooltip title="View employee details" arrow>
                                  <IconButton size="small" onClick={() => handleEmployeeQuickView(emp)} sx={{ width: 20, height: 20, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            {/* Timeline Row */}
                              <Box sx={{ flex: 1, position: 'relative', minWidth: containerWidth, zIndex: 1, height: `${layout.rowHeight}px`, boxSizing: 'border-box' }}>
                                {/* Per-cell interactive hotspots removed for ungrouped employee rows */}
                              <DroppableTimelineRow
                                employee={emp}
                                dept={deptId}
                                shifts={empShifts}
                                roleMap={roleMap}
                                onContextMenu={openMenu}
                                date={selectedDate}
                                onShiftResize={handleShiftResize}
                                visibleMinutes={visibleMinutes}
                                rangeStartMinutes={rangeStartMinutes}
                                rangeEndMinutes={rangeEndMinutes}
                                containerWidth={containerWidth}
                                layoutTick={layoutTick}
                                isWeekPublished={isWeekPublished}
                                availability={availability?.availability}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Collapse>
              </Box>
            );
          })}
          </Box>
        </Box>
      </Paper>

      {/* Employee Quick View Dialog */}
      <EmployeeQuickView
        employee={quickViewEmployee}
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        locations={locations}
        scheduleConflicts={quickViewEmployee ? calculateScheduleConflicts(quickViewEmployee.id, shifts) : []}
      />
  {/* Debug floating Add button removed per user request */}
  {/* per-row centered Add (+) overlays are rendered inside each timeline row box */}
    </Box>
  );
}
